import { PaintTarget } from '@ff-os/rendering-shared/targets';
import { calculateCoverCropForFfCanvas } from '@ff-os/shared-utils/crop/calculateCoverCropForFfCanvas';
import { Area } from '@ff-os/shared-utils/types/Area';
import { Size } from '@ff-os/shared-utils/types/Size';
import { degreesToRadians } from '@ff-os/shared-utils/units';
import { rotateAroundOrigin } from '@ff-os/shared-utils/vector2d';
import { Photo } from 'ff-canvas';
import { PhotoPrintsJson } from './PhotoPrintsJson';
import { getRotationSettings, RotationSettings } from './rotationSettings';
import { StrokeSettings } from './StrokeSettings';
import { toPhotoUrl } from './toPhotoUrl';

export const toPaintable = (
    photo: PhotoPrintsJson.ProcessedPhoto,
    size: Size,
    jwt: string | null,
    target: PaintTarget,
    border?: StrokeSettings
): Photo => {

    const rotationSettings = getRotationSettings(size, photo, target);

    const imageSize = {
        width: photo.width,
        height: photo.height
    };

    const imageContainer = toImageContainer(size, rotationSettings, border);
    const cropOverrides = calculateCoverCropForFfCanvas(imageContainer, imageSize, photo.crop);

    return {
        ...createPhoto(photo, imageContainer, jwt, rotationSettings, border),
        ...cropOverrides,
        ...toPrintOverrides(target)
    };
};

export const toPaintableWithoutCropping = (
    photo: PhotoPrintsJson.ProcessedPhoto,
    size: Size,
    jwt: string | null
) => {

    const area: Area = {
        width: size.width,
        height: size.height,
        x: -0.5 * size.width,
        y: -0.5 * size.height
    };

    return createPhoto(photo, area, jwt);
};

const createPhoto = (
    photo: PhotoPrintsJson.ProcessedPhoto,
    imageContainer: Area,
    jwt: string | null,
    settings?: RotationSettings,
    strokeSettings?: StrokeSettings
): Photo => {

    const angle = settings ? settings.imageOrientation : 0;

    if (!strokeSettings) {
        strokeSettings = {color: '#fff', size: 0};
    }

    return ({

        id: photo.id,
        type: 'photo',
        round: false,

        url: toPhotoUrl(photo, jwt)!,

        x: calculateXBasedOnAngle(imageContainer.x, angle, strokeSettings),
        y: imageContainer.y - strokeSettings.size,
        width: imageContainer.width,
        height: imageContainer.height,
        zIndex: 0,
        angle: degreesToRadians(angle),

        scaleX: 1,
        scaleY: 1,
        alpha: 1,

        filter: photo.filter || null,
        brightness: 1,
        saturation: 1,
        contrast: 1,

        clipPaths: [],
        clipViewBoxWidth: 0,
        clipViewBoxHeight: 0,

        frameUrl: null,
        maskUrl: null,

        innerOffsetX: 0,
        innerOffsetY: 0,
        innerZoom: 1,
        innerAngle: 0,

        flipX: false,
        flipY: false,

        shadow: false,
        shadowBlur: 0,
        shadowColor: '#FFF',
        shadowOffsetX: 0,
        shadowOffsetY: 0,

        stroke: strokeSettings.size > 0,
        strokeSize: strokeSettings.size,
        strokeStyle: strokeSettings.color,

        fit: 'cover',

        custom: {
            watch: [],
            values: {}
        }
    });
};

const toImageContainer = (size: Size, settings: RotationSettings, strokeSettings?: StrokeSettings) => {

    const width = (settings.rotateImage ? size.height : size.width) - (strokeSettings?.size ?? 0 * 2);
    const height = (settings.rotateImage ? size.width : size.height) - (strokeSettings?.size ?? 0 * 2) ;
    const imageAngle = settings.imageOrientation;

    const topLeft = {
        x: -0.5 * width,
        y: -0.5 * height
    };
 
    const center = {
        x: topLeft.x + 0.5 * width,
        y: topLeft.y + 0.5 * height
    };

    const rotatedTopLeft = rotateAroundOrigin(topLeft, center, imageAngle);

    return {
        x: rotatedTopLeft.x,
        y: rotatedTopLeft.y,
        width: width,
        height: height
    };
};

const toPrintOverrides = (target: PaintTarget): Partial<Photo> => {

    if (target === 'print') {
        return {
            svgOptions: {
                encoding: 'jpeg',
                quality: 0.9
            }
        };
    }

    return {};
};

const calculateXBasedOnAngle = (x: number, angle: number, strokeSettings?: StrokeSettings) : number => {

    if (!strokeSettings) {
        return x;
    }
    
    if (angle === 0) {
        return x - strokeSettings.size;
    } else {
        return x + strokeSettings.size;
    }
};